import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, Upload, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.echt.gold';

const Kontakt = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone || '');
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);
      
      // Append images
      images.forEach((image) => {
        formDataToSend.append('images', image);
      });

      await axios.post(`${API}/contact-with-images`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Nachricht erfolgreich gesendet! Wir melden uns zeitnah bei Ihnen.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setImages([]);
      setImagePreviews([]);
    } catch (error) {
      console.error(error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const fileType = file.type.toLowerCase();
      return fileType === 'image/jpeg' || fileType === 'image/jpg' || fileType === 'image/png';
    });

    if (validFiles.length !== files.length) {
      toast.error('Nur JPG, JPEG und PNG Dateien sind erlaubt!');
    }

    // Limit to 5 images
    if (images.length + validFiles.length > 5) {
      toast.error('Maximal 5 Bilder erlaubt!');
      return;
    }

    setImages([...images, ...validFiles]);

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  return (
    <>
      <Helmet>
        <title>Kontakt - Echt Gold | Jetzt Termin vereinbaren</title>
        <meta name="description" content="Kontaktieren Sie Echt Gold für eine kostenlose Bewertung. Telefon, E-Mail oder Kontaktformular - wir sind für Sie da!" />
        <link rel="canonical" href={`${BASE_URL}/kontakt`} />
      </Helmet>

      <div className="bg-black/90 py-8 border-b border-gold/20">
        <div className="container mx-auto px-4">
          <div className="text-sm text-gray-400 mb-2">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-gold">Kontakt</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gold-text">Kontakt</h1>
        </div>
      </div>

      <section className="section section-dark">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6 gold-text">Nehmen Sie Kontakt auf</h2>
              <p className="text-gray-300 mb-8 text-lg">
                Haben Sie Fragen oder möchten Sie einen Termin vereinbaren? Wir freuen uns auf Ihre Nachricht!
              </p>

              <div className="space-y-6">
                <div className="card">
                  <div className="flex gap-4 items-start">
                    <MapPin className="text-gold flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold mb-2">Adresse</h3>
                      <p className="text-gray-300">
                        Echt Gold<br />
                        Neben dem Brand 10<br />
                        44135 Dortmund
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex gap-4 items-start">
                    <Phone className="text-gold flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold mb-2">Telefon</h3>
                      <a href="tel:023139085116" className="text-gray-300 hover:text-gold transition-colors">
                        0231 39085116
                      </a>
                      <p className="text-sm text-gray-400 mt-1">WhatsApp verfügbar</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex gap-4 items-start">
                    <Mail className="text-gold flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold mb-2">E-Mail</h3>
                      <a href="mailto:Info@echtgold.de" className="text-gray-300 hover:text-gold transition-colors">
                        Info@echtgold.de
                      </a>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex gap-4 items-start">
                    <Clock className="text-gold flex-shrink-0" size={24} />
                    <div>
                      <h3 className="font-bold mb-2">Öffnungszeiten</h3>
                      <p className="text-gray-300">
                        Montag - Freitag: 08:00 - 18:00 Uhr<br />
                        Samstag: 09:00 - 17:00 Uhr<br />
                        Sonntag: Geschlossen
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Termine nach Vereinbarung auch außerhalb der Öffnungszeiten möglich
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="card">
                <h2 className="text-2xl font-bold mb-6 gold-text">Kontaktformular</h2>
                <form onSubmit={handleSubmit}>
                  <div className="input-group">
                    <label className="input-label">Name *</label>
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-field" 
                      required 
                      data-testid="contact-name"
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">E-Mail *</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-field" 
                      required 
                      data-testid="contact-email"
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Telefon</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field" 
                      data-testid="contact-phone"
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Betreff *</label>
                    <input 
                      type="text" 
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="input-field" 
                      required 
                      data-testid="contact-subject"
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Nachricht *</label>
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="input-field" 
                      rows="5"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Bilder hochladen (optional)</label>
                    <div className="border-2 border-dashed border-gold/30 rounded-lg p-4 text-center hover:border-gold/50 transition-colors">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                        id="image-upload"
                        data-testid="contact-images"
                      />
                      <label 
                        htmlFor="image-upload" 
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="text-gold mb-2" size={32} />
                        <span className="text-gray-300 text-sm">
                          Klicken Sie hier oder ziehen Sie Bilder hierher
                        </span>
                        <span className="text-gray-500 text-xs mt-1">
                          JPG, JPEG oder PNG • Max. 5 Bilder • Max. 5MB pro Bild
                        </span>
                      </label>
                    </div>

                    {imagePreviews.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 gap-3">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img 
                              src={preview} 
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-gold/30"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`remove-image-${index}`}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary w-full" 
                    disabled={loading}
                    data-testid="submit-contact"
                  >
                    <Send className="inline mr-2" size={18} />
                    {loading ? 'Wird gesendet...' : 'Nachricht senden'}
                  </button>

                  <p className="text-sm text-gray-400 mt-4 text-center">
                    Wir melden uns innerhalb von 24 Stunden bei Ihnen zurück
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Kontakt;